/**
 * Session token accessor — single source of truth for reading the
 * post-login session token from browser storage.
 *
 * Backend REST endpoints (Authorization: Bearer <token>) and Socket.IO
 * namespaces (auth.token) share this token via
 * services/auth_service.validate_session on the backend.
 *
 * 2026-07-06 — extracted so useWorkspaceSocket can share the pattern
 * that AfikimSocket (utils/socket.js) already uses. Existing inline
 * call sites are NOT refactored in this PR (out of scope); they can
 * be consolidated in a follow-up.
 */

export function getSessionToken() {
  try {
    return (
      localStorage.getItem('session_token') ||
      sessionStorage.getItem('session_token') ||
      ''
    );
  } catch {
    // localStorage / sessionStorage may throw in privacy-mode iframes
    // or when storage access is denied. Treat as "no token" — the caller
    // decides whether to send an auth object at all.
    return '';
  }
}

/**
 * Authorization headers for a REST call, or `{}` when there is no token.
 *
 * 2026-08-30. Added because DELETE had no way to say who was asking.
 *
 * The server resolves the acting user from `Authorization: Bearer <token>`
 * or a `session_id` cookie (`unified_server.get_session_id`). This app
 * authenticates with the token above, not a cookie — so a fetch that omits
 * the header is anonymous to the server no matter what `credentials` says.
 *
 * That did not show on CREATE or UPDATE, because those carry identity in the
 * request body (`CreatedById` / `LastModifiedById`). DELETE has no body, so
 * `recycle_bin.deleted_by` recorded 'unknown' for 1,636 of 1,641 line-item
 * deletions — every one in July and August — and the history could not say
 * who removed anything.
 *
 * Spread it into the headers of any call whose actor matters:
 *
 *     fetch(url, { method: 'DELETE', headers: { ...authHeaders() } })
 */
export function authHeaders() {
  const token = getSessionToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** How a deletion happened, for the audit trail. */
export const DELETE_MANUAL = 'manual';
export const DELETE_AUTOMATIC = 'automatic';

/**
 * Headers that tell the server WHO is deleting and HOW.
 *
 * 2026-08-30. `authHeaders` alone was not enough. The owner deleted one
 * product and the recycle bin recorded two rows a second apart, both as
 * meni@afikim.my — his delete, and the inline-filter reconciliation removing
 * the filter. Identical rows, so a history built on them would report the
 * second as something he did by hand.
 *
 * `deleted_by` answers who set it in motion and is right for both. These
 * headers answer HOW, which the server cannot work out for itself: the two
 * requests are otherwise byte-identical.
 *
 *     deleteHeaders('OLI_DELETE')                                → manual
 *     deleteHeaders('OLI_DELETE_RECONCILE', DELETE_AUTOMATIC)    → automatic
 *
 * Mode defaults to manual, so an untagged caller keeps working. An automatic
 * deletion MUST name its cause — the server downgrades a causeless one to
 * manual rather than record that the system acted for no stated reason.
 */
export function deleteHeaders(source, mode = DELETE_MANUAL) {
  const headers = { ...authHeaders(), 'X-Afikim-Delete-Mode': mode };
  if (source) headers['X-Afikim-Delete-Source'] = source;
  return headers;
}
